// framework/client/helpers/MessagingClient/ChatViewHeader.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { createAvatarElement } from "../avatarHelper.js";

export class ChatViewHeader extends ComponentBase {
    static styles = `
        .chat-view-header {
            display: flex;
            align-items: center;
            padding: 0.5rem 1rem; /* Reduced padding */
            border-bottom: 1px solid var(--bs-border-color);
            background-color: var(--bs-tertiary-bg); /* Subtle background */
            flex-shrink: 0; /* Prevent shrinking */
        }
        .back-button-mobile {
            margin-right: 0.75rem;
            font-size: 1.5rem;
            color: var(--bs-emphasis-color);
            display: none; /* Hidden by default, shown on mobile by parent */
        }
        
        .chat-header-info {
            flex-grow: 1;
            overflow: hidden;
        }
        .chat-header-name {
            font-weight: 500;
            color: var(--bs-emphasis-color);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .chat-header-status { /* For online status or member count */
            font-size: 0.75rem;
            color: var(--bs-secondary-color);
        }
        .chat-header-actions .dropdown .btn-link { /* Ensure dropdown toggle button is styled like other action buttons */
            background: none;
            border: none;
            color: var(--bs-secondary-color);
            font-size: 1.25rem;
            padding: 0.25rem 0.5rem;
            line-height: 1;
        }
        .chat-header-actions .dropdown .btn-link:hover {
            color: var(--bs-primary);
        }
        .chat-header-actions .dropdown-menu { /* Style for the dropdown menu itself */
            min-width: 10rem; /* Or whatever fits your items */
        }


        /* Responsive: Show back button on mobile */
        @media (max-width: 768px) {
            .back-button-mobile {
                display: inline-block;
            }
        }
    `;

    constructor(config) { // Expects { appClient, conversationData, instanceId }
        super(config);
        this.conversationData = this.config.conversationData;
        this.dropdownInstance = null; // To store Bootstrap dropdown instance
        this.typingUsers = new Map(); // contactId -> { displayName, timeoutId }
        this.TYPING_INDICATOR_TIMEOUT_MS = 5000; // Clear indicator if no update after 5s

        this._handleHeaderClick = this.handleHeaderClick.bind(this);

        this.applyStyles();
    }

    async initialize() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'chat-view-header';
            // --- Attach delegated event listener to the container ---
            this.container.addEventListener('click', this._handleHeaderClick);
        }

        await super.initialize();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'chat-view-header';
            this.container.addEventListener('click', this._handleHeaderClick);
        }
        this.container.innerHTML = '';

        const convData = this.conversationData || { displayName: "Chat", displayAvatar: null, channelType: "direct_message", members: [] };

        const avatarElement = createAvatarElement({
            displayName: convData.displayName,
            avatarUrl: convData.displayAvatar, // displayAvatar should be relative path
            additionalClasses: ['m-2']
        });

        let actionsHtml = '';
        if (convData.channelType === 'direct_message' && convData.otherParticipantType === 'agent' && convData.otherParticipantIdentityId) {
            actionsHtml += `
                <button class="btn btn-link" 
                        data-action="view-agent-screen" 
                        data-identity-id-view="${convData.otherParticipantIdentityId}"
                        title="${this.t('View Agent Screen')}">
                    <i class="bi bi-display"></i>
                </button>
            `;
        }

        if (convData.channelType === 'group') {
            actionsHtml += `
                <div class="dropdown">
                    <button class="btn btn-link dropdown-toggle" type="button" id="${this.config.instanceId}-groupActionsDropdown" data-bs-toggle="dropdown" aria-expanded="false" title="${this.t('More options')}" data-i18n-title="More options">
                        <i class="bi bi-three-dots-vertical"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="${this.config.instanceId}-groupActionsDropdown">
                        <li><a class="dropdown-item" href="#" data-action="group-settings" data-i18n="Group Settings">${this.t('Group Settings')}</a></li>
                    </ul>
                </div>
            `;
        } else {
            // Wrap non-group actions in a div to maintain layout
            actionsHtml = `<div class="d-flex align-items-center">${actionsHtml}</div>`;
        }

        let headerInfoClickableClass = '';
        let headerInfoDataAttributes = '';
        if (convData.channelType === 'direct_message' && convData.members) {
            const myContactId = this.appClient.getSharedData('myMessagingContactId');
            const otherMember = convData.members.find(m => m.contactId !== myContactId);
            if (otherMember) {
                headerInfoClickableClass = 'cursor-pointer';
                headerInfoDataAttributes = `data-action-view-contact-info="true" data-contact-id-view="${otherMember.contactId}"`;
            }
        }

        const backButtonHtml = `<button class="back-button-mobile" title="${this.t('Back')}" data-i18n-title="Back" data-action="back"><i class="bi bi-arrow-left"></i></button>`;

        // Manually construct the innerHTML carefully to include the avatarElement's HTML
        const infoDiv = document.createElement('div');
        infoDiv.className = `chat-header-info ${headerInfoClickableClass}`;
        infoDiv.innerHTML = `<div class="chat-header-name">${convData.displayName}</div><div class="chat-header-status"></div>`;
        if (headerInfoDataAttributes) { // Add data attributes if they exist
            const attrs = headerInfoDataAttributes.match(/data-\S+?="[^"]+"/g);
            if (attrs) attrs.forEach(attr => {
                const [key, value] = attr.split('=');
                infoDiv.setAttribute(key, value.slice(1, -1)); // remove quotes
                avatarElement.setAttribute(key, value.slice(1, -1));
            });
        }


        this.container.innerHTML = backButtonHtml;
        this.container.appendChild(avatarElement); // Append the avatar node
        this.container.appendChild(infoDiv);       // Append the info node
        this.container.insertAdjacentHTML('beforeend', `<div class="chat-header-actions">${actionsHtml}</div>`);

        this.statusElement = this.container.querySelector('.chat-header-status');
        this.updateTypingDisplay();

        // ... (dropdown init, translations) ...
        const dropdownToggleEl = this.container.querySelector(`#${this.config.instanceId}-groupActionsDropdown`);
        if (dropdownToggleEl) {
            if (this.dropdownInstance) this.dropdownInstance.dispose();
            this.dropdownInstance = new bootstrap.Dropdown(dropdownToggleEl);
        }
        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }



    updateConversationData(newConvData) {
        // Only re-render if the data has actually changed to avoid unnecessary DOM manipulation
        if (JSON.stringify(this.conversationData) !== JSON.stringify(newConvData)) {
            this.conversationData = newConvData;
            if (this.container) { // Ensure container is available
                this.render(); // Always fully re-render the header content when conversation data changes
            }
        } else if (this.container && !this.statusElement) {
            // If data is same but statusElement somehow not set (e.g. initial render race), still render
            this.render();
        } else {
            // If only typing status needs update and data is the same
            this.updateTypingDisplay();
        }
    }

    updateTypingStatus({ contactId, displayName, isTyping }) {
        if (isTyping) {
            if (this.typingUsers.has(contactId)) {
                clearTimeout(this.typingUsers.get(contactId).timeoutId);
            }
            const timeoutId = setTimeout(() => {
                this.typingUsers.delete(contactId);
                this.updateTypingDisplay();
            }, this.TYPING_INDICATOR_TIMEOUT_MS);
            this.typingUsers.set(contactId, { displayName, timeoutId });
        } else {
            if (this.typingUsers.has(contactId)) {
                clearTimeout(this.typingUsers.get(contactId).timeoutId);
                this.typingUsers.delete(contactId);
            }
        }
        this.updateTypingDisplay();
    }

    updateTypingDisplay() {
        if (!this.statusElement) return;

        if (this.typingUsers.size > 0) {
            const names = Array.from(this.typingUsers.values()).map(u => u.displayName);
            let text;
            if (names.length === 1) {
                text = this.t("{{name}} is typing...", { name: names[0] });
            } else if (names.length === 2) {
                text = this.t("{{name1}} and {{name2}} are typing...", { name1: names[0], name2: names[1] });
            } else {
                text = this.t("Multiple people are typing...");
            }
            this.statusElement.textContent = text;
            this.statusElement.classList.add('typing');
        } else {
            // Show default status (e.g., member count or online status if available)
            // For now, just clear it or show a generic status
            const memberCount = this.conversationData?.members?.length;
            if (this.conversationData?.channelType === 'group' && memberCount) {
                this.statusElement.textContent = this.t("{{count}} members", { count: memberCount });
            } else if (this.conversationData?.channelType === 'direct_message') {
                this.statusElement.textContent = this.t('Direct Message'); // Or online status if available
            }
            else {
                this.statusElement.textContent = ''; // Clear if no specific status
            }
            this.statusElement.classList.remove('typing');
        }
        this.appClient.tx.applyTranslations(this.statusElement);
    }

    handleHeaderClick(event) {
        const backButton = event.target.closest('[data-action="back"]');
        if (backButton) {
            this.emit('backClicked');
            return;
        }

        const groupSettingsLink = event.target.closest('[data-action="group-settings"]');
        if (groupSettingsLink) {
            event.preventDefault();
            this.emit('groupSettingsClicked', { channelId: this.conversationData.channelId });
            return;
        }

        // --- START MODIFICATION ---
        const viewScreenButton = event.target.closest('[data-action="view-agent-screen"]');
        if (viewScreenButton) {
            const identityId = viewScreenButton.dataset.identityIdView;
            if (identityId) {
                this.appClient.events.fireLocal('DISPLAY_REMOTE_SCREEN', { identityId: identityId });
            }
            return;
        }
        // --- END MODIFICATION ---

        const contactInfoTarget = event.target.closest('[data-action-view-contact-info]');
        if (contactInfoTarget) {
            const contactId = contactInfoTarget.dataset.contactIdView; // Use a unique attribute
            if (contactId) {
                this.emit('contactInfoClicked', { contactId });
            }
            return;
        }
    }

    destroy() {
        if (this.dropdownInstance) {
            this.dropdownInstance.dispose();
            this.dropdownInstance = null;
        }
        // --- Remove the delegated event listener ---
        if (this.container) {
            this.container.removeEventListener('click', this._handleHeaderClick);
        }
        // ... (clear typing timeouts)
        this.typingUsers.forEach(user => clearTimeout(user.timeoutId));
        this.typingUsers.clear();
        super.destroy();

    }
}